import React, {
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react"
import { DESIGN_W } from "./constants.js"
import {
  getThemeCssVariables,
  getNodeChildren,
  measureNode,
  getBreakpointForWidth,
  findNodeUnderCursor,
} from "./utils.js"
import { getCurrentTheme, onThemeChange } from "./utils/theme-helper.js"
import PropertyPanel from "./components/PropertyPanel.jsx"
import PageSettingsDialog from "./components/PageSettingsDialog.jsx"
import { SaveCMSContent } from "aldoo-ra/CMS/api"
import { showNotification } from "aldoo-ra/Notification"
import { postFormatFields } from "aldoo-ra/CMS/field-formatter"
import TypeResolver from "aldoo-ra/CMS/type-resolver"

// State Hooks
import { useEditorState } from "./state/useEditorState.js"
import { useSelection } from "./state/useSelection.js"
import { useHistory } from "./state/useHistory.js"
import { useContextStack } from "./state/useContextStack.js"
import { useInteraction } from "./state/useInteraction.js"
import { usePrefabs } from "./state/usePrefabs.js"

// Services
import { LayoutCalculator } from "./services/LayoutCalculator.js"
import { SelectionManager } from "./services/SelectionManager.js"
import { CoordinateMapper } from "./services/CoordinateMapper.js"
import { PrefabManager } from "./services/PrefabManager.js"

// Operations
import { useNodeOperations } from "./operations/useNodeOperations.js"
import { usePrefabOperations } from "./operations/usePrefabOperations.js"
import { useHierarchyOperations } from "./operations/useHierarchyOperations.js"
import { useInteractionHandlers } from "./operations/useInteractionHandlers.js"

// Controllers
import { usePropertyPanelController } from "./controllers/PropertyPanelController.js"
import { useLeftSidebarController } from "./controllers/LeftSidebarController.js"
import { useTopToolbarController } from "./controllers/TopToolbarController.js"
import { useVariantPopupController } from "./controllers/VariantPopupController.js"
import { useNodeItemController } from "./controllers/NodeItemController.js"

// Feature Components
import TopToolbar from "./features/toolbar/TopToolbar.jsx"
import LeftSidebar from "./features/sidebar/LeftSidebar.jsx"
import SelectionBox from "./features/selection/SelectionBox.jsx"

// Components
import SnapLinesOverlay from "./components/SnapLinesOverlay.jsx"
import GizmoOverlay from "./components/GizmoOverlay.jsx"
import NodeItem from "./components/NodeItem.jsx"
import ContextMenu from "./components/ContextMenu.jsx"
import VariantPopup from "./components/VariantPopup.jsx"
import { Icons } from "./components/Icon.jsx"

// Event Handler Hooks
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts.js"
import { useCanvasPanning } from "./hooks/useCanvasPanning.js"
import { useNodeDragging } from "./hooks/useNodeDragging.js"
import { useNodeResizing } from "./hooks/useNodeResizing.js"
import { useMouseTracking } from "./hooks/useMouseTracking.js"
import { useDragAndDrop } from "./hooks/useDragAndDrop.js"
import { useVariantManager } from "./hooks/useVariantManager.js"
import { useEditorBridge } from "./hooks/useEditorBridge.js"

const VBECSEditor = ({ page, onSave, onClose }) => {
  // State
  const [themeVars, setThemeVars] = useState(() =>
    getThemeCssVariables(getCurrentTheme())
  )

  const [canvasContextMenu, setCanvasContextMenu] = useState(null)
  const [variantPopup, setVariantPopup] = useState(null)

  useEffect(() => {
    const unsubscribe = onThemeChange((theme) => {
      setThemeVars(getThemeCssVariables(theme))
    })
    // Initial sync
    setThemeVars(getThemeCssVariables(getCurrentTheme()))
    return unsubscribe
  }, [])

  const editorState = useEditorState(page)
  const selection = useSelection([])
  const history = useHistory()
  const interaction = useInteraction()
  const prefabState = usePrefabs()

  // Context stack with callback to switch to hierarchy tab when entering isolation mode
  const contextStack = useContextStack(() => {
    editorState.setActiveSidebarTab("hierarchy")
  })

  const {
    nodes,
    setNodes,
    pageRef,
    canvasRef,
    scrollContainerRef,
    savedEditViewRef,
    isSaving,
    setIsSaving,
    showSettings,
    setShowSettings,
    activeSidebarTab,
    setActiveSidebarTab,
    getPageDimensions,
    getEditorSettings,
    viewportWidth,
    setViewportWidth,
    activeBreakpoint,
    setActiveBreakpoint,
    collapsedNodeIds,
    toggleNodeExpansion,
  } = editorState
  const { selectedNodeIds, setSelectedNodeIds, clearSelection } = selection

  // MCP editor bridge — dev only, silent when bridge server is not running
  useEditorBridge({ nodes, selectedNodeIds, page, setNodes, setSelectedNodeIds })

  const {
    executeCommand,
    recordCommand,
    undo,
    redo,
    clear: clearHistory,
    createUpdateNodesCommand,
    createUpdatePrefabsCommand,
    createCompositeCommand,
  } = history
  const { contextStack: stack, setContextStack, pushContext } = contextStack
  const {
    isLiveMode,
    setIsLiveMode,
    isSpacePressed,
    setIsSpacePressed,
    canvasScale,
    selectionBox,
    snapLines,
    setSnapLines,
    draggingRef,
    resizingRef,
    panRef,
    interactionData,
    startSelectionBox,
    updateSelectionBox,
    stopSelectionBox,
    saveInteractionState,
    resetInteraction,
  } = interaction
  const {
    prefabs,
    setPrefabs,
    selectedPrefabId,
    setSelectedPrefabId,
    prefabSearchTerm,
    setPrefabSearchTerm,
    editingPrefabId,
    setEditingPrefabId,
    editingName,
    setEditingName,
    getFilteredPrefabs,
  } = prefabState

  const { getContentType } = TypeResolver()
  const aldooPageType = getContentType("AldooPage")

  const [containerDimensions, setContainerDimensions] = useState({
    width: 1280,
    height: 720,
  })
  const resizeObserverRef = useRef(null)

  const setScrollContainer = useCallback((node) => {
    scrollContainerRef.current = node
    if (resizeObserverRef.current) resizeObserverRef.current.disconnect()

    if (node) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setContainerDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          })
        }
      })
      resizeObserverRef.current.observe(node)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) resizeObserverRef.current.disconnect()
    }
  }, [])

  useEffect(() => {
    if (page) {
      pageRef.current = page
      if (page.content) {
        if (page.content.ecs_nodes) {
          setNodes(page.content.ecs_nodes)
          clearHistory()
        }
        if (page.content.ecs_prefabs) {
          // Migrate old prefabs with PrefabRoot wrapper to new format
          const migratedPrefabs = PrefabManager.migratePrefabs(
            page.content.ecs_prefabs
          )
          setPrefabs(migratedPrefabs)
        }
      }
    }
  }, [page])

  const getContext = useCallback(() => {
    let currentNodes = nodes
    let isPrefab = false
    let currentPrefabId
    let container
    let targetPrefabId
    let editingInstanceId = null // Track if we're editing an instance (for overrides)
    let contextLabel = "Root"

    for (const ctx of stack) {
      let node = currentNodes.find((n) => n.id === ctx.id)

      // Fallback: Check if ctx.id is a Prefab Definition (Direct Edit Mode)
      if (!node) {
        const prefabDef = prefabs.find((p) => p.id === ctx.id)
        if (prefabDef) {
          currentNodes = prefabDef.nodes
          currentPrefabId = prefabDef.id
          targetPrefabId = prefabDef.id
          // Check if this context has an instanceId (entered via double-click on instance)
          if (ctx.instanceId) {
            editingInstanceId = ctx.instanceId
          }
          container = null
          continue
        }
      }

      if (!node) break

      const hasLocalChildren = getNodeChildren(currentNodes, node.id).length > 0

      // Check if this node is a prefab instance
      if (node.props?.prefabId) {
        const def = prefabs.find((p) => p.id === node.props.prefabId)
        if (def) {
          // Switch to definition nodes for editing
          currentNodes = def.nodes
          currentPrefabId = def.id
          targetPrefabId = def.id
          editingInstanceId = node.id // Track that we're editing this instance
          container = node
        }
      } else if (node.type === "Box" || hasLocalChildren) {
        container = node
      }
    }

    const updateNodesInternal = (newNodes) => {
      if (targetPrefabId && editingInstanceId) {
        // Editing a specific prefab instance - store changes as overrides
        // Map definition node changes to instance overrides
        const updatedPrefabDef = prefabs.find((p) => p.id === targetPrefabId)
        if (!updatedPrefabDef) return

        // Find what changed in the definition nodes
        const defNodes = updatedPrefabDef.nodes
        const overridesToApply = {}

        newNodes.forEach((newNode) => {
          const oldDefNode = defNodes.find((n) => n.id === newNode.id)
          if (!oldDefNode) return

          // Check what props changed
          const oldProps = oldDefNode.props || {}
          const newProps = newNode.props || {}
          const metaProps = [
            "sourceNodeId",
            "prefabId",
            "overrides",
            "x",
            "y",
            "width",
            "height",
            "anchors",
          ]
          const changedProps = {}

          Object.keys(newProps).forEach((key) => {
            if (!metaProps.includes(key) && newProps[key] !== oldProps[key]) {
              changedProps[key] = newProps[key]
            }
          })

          if (Object.keys(changedProps).length > 0) {
            overridesToApply[newNode.id] = changedProps
          }
        })

        // Apply overrides to the specific instance
        if (Object.keys(overridesToApply).length > 0) {
          console.log(
            "[Instance Override] Applying overrides to instance:",
            editingInstanceId,
            overridesToApply
          )
          setNodes((currentNodes) => {
            return currentNodes.map((node) => {
              // Find the instance root
              if (node.id === editingInstanceId) {
                const rootOverrides =
                  overridesToApply[updatedPrefabDef.nodes[0].id] || {}
                return {
                  ...node,
                  props: {
                    ...node.props,
                    overrides: {
                      ...(node.props.overrides || {}),
                      ...rootOverrides,
                    },
                  },
                }
              }

              // Find instance children by sourceNodeId
              if (
                node.props?.sourceNodeId &&
                overridesToApply[node.props.sourceNodeId]
              ) {
                const nodeOverrides = overridesToApply[node.props.sourceNodeId]
                // Check if this node belongs to the instance we're editing
                // by traversing up to find the root
                let current = node
                let belongsToInstance = false
                const visited = new Set()
                while (current && !visited.has(current.id)) {
                  visited.add(current.id)
                  if (current.id === editingInstanceId) {
                    belongsToInstance = true
                    break
                  }
                  current = currentNodes.find((n) => n.id === current.parentId)
                }

                if (belongsToInstance) {
                  return {
                    ...node,
                    props: {
                      ...node.props,
                      overrides: {
                        ...(node.props.overrides || {}),
                        ...nodeOverrides,
                      },
                    },
                  }
                }
              }

              return node
            })
          })
        }
      } else if (targetPrefabId) {
        // Editing a prefab definition (not a specific instance) - update all instances
        // 1. Update the Target Prefab Definition
        const updatedPrefabDef = prefabs.find((p) => p.id === targetPrefabId)
        // Safety check if prefab was deleted or not found
        if (!updatedPrefabDef) return

        const newPrefabDef = { ...updatedPrefabDef, nodes: newNodes }

        let newPrefabsList = prefabs.map((p) =>
          p.id === targetPrefabId ? newPrefabDef : p
        )

        // 2. Sync instances in the Scene (Root)
        // Use functional update to get the latest nodes state
        setNodes((currentNodes) => {
          const sceneSync = PrefabManager.syncInstances(
            newPrefabDef,
            currentNodes
          )
          if (
            sceneSync.nodesToAdd.length > 0 ||
            sceneSync.nodesToUpdate.length > 0 ||
            sceneSync.nodesToRemove.length > 0
          ) {
            let updatedSceneNodes = currentNodes.filter(
              (n) => !sceneSync.nodesToRemove.includes(n.id)
            )
            updatedSceneNodes = updatedSceneNodes.map((n) => {
              const updated = sceneSync.nodesToUpdate.find(
                (un) => un.id === n.id
              )
              return updated || n
            })
            updatedSceneNodes = [...updatedSceneNodes, ...sceneSync.nodesToAdd]
            return updatedSceneNodes
          }
          return currentNodes
        })

        // 3. Sync instances in OTHER Prefabs (Nested Prefabs)
        // We iterate newPrefabsList to update any other prefabs that might contain instances of this one
        newPrefabsList = newPrefabsList.map((p) => {
          if (p.id === targetPrefabId) return p // Already updated definition

          const pSync = PrefabManager.syncInstances(newPrefabDef, p.nodes)
          if (
            pSync.nodesToAdd.length > 0 ||
            pSync.nodesToUpdate.length > 0 ||
            pSync.nodesToRemove.length > 0
          ) {
            let updatedPNodes = p.nodes.filter(
              (n) => !pSync.nodesToRemove.includes(n.id)
            )
            updatedPNodes = updatedPNodes.map((n) => {
              const updated = pSync.nodesToUpdate.find((un) => un.id === n.id)
              return updated || n
            })
            updatedPNodes = [...updatedPNodes, ...pSync.nodesToAdd]
            return { ...p, nodes: updatedPNodes }
          }
          return p
        })

        setPrefabs(newPrefabsList)
      } else {
        // Editing scene nodes - check if any are prefab instances and track overrides
        const updatedNodes = newNodes.map((node) => {
          // Find the old version to see what changed
          const oldNode = nodes.find((n) => n.id === node.id)
          if (!oldNode) {
            return node
          }

          // Check if this is a prefab instance (root or child)
          const isPrefabRoot = oldNode.props?.prefabId
          const isPrefabChild = oldNode.props?.sourceNodeId

          if (!isPrefabRoot && !isPrefabChild) {
            // Not a prefab instance, return as is
            return node
          }

          // This is a prefab instance - detect property changes and store as overrides
          const oldProps = oldNode.props || {}
          const newProps = node.props || {}
          const existingOverrides = oldProps.overrides || {}

          // Find changed properties (excluding meta properties)
          const metaProps = [
            "sourceNodeId",
            "prefabId",
            "overrides",
            "x",
            "y",
            "width",
            "height",
            "anchors",
          ]
          const changedProps = {}

          Object.keys(newProps).forEach((key) => {
            if (!metaProps.includes(key) && newProps[key] !== oldProps[key]) {
              changedProps[key] = newProps[key]
            }
          })

          if (Object.keys(changedProps).length > 0) {
            // Properties changed - merge with existing overrides
            return {
              ...node,
              props: {
                ...newProps,
                overrides: {
                  ...existingOverrides,
                  ...changedProps,
                },
              },
            }
          }

          return node
        })

        setNodes(updatedNodes)
      }
    }

    return {
      activeNodes: currentNodes,
      containerNode: container,
      currentPrefabId,
      targetPrefabId,
      editingInstanceId,
      updateNodes: updateNodesInternal,
      contextLabel,
    }
  }, [nodes, prefabs, stack, setPrefabs, setNodes])

  const {
    activeNodes,
    containerNode,
    currentPrefabId,
    targetPrefabId,
    editingInstanceId,
    updateNodes,
    contextLabel,
  } = getContext()

  const getRootDimensions = useCallback(() => {
    const { pageWidth, pageHeight } = getPageDimensions()
    const dims = LayoutCalculator.getRootDimensions(
      stack,
      containerNode,
      pageWidth,
      pageHeight
    )

    // Explicit Viewport Width overrides everything (Simulation Mode)
    if (viewportWidth !== "100%" && viewportWidth !== "auto") {
      const vw = parseInt(viewportWidth)
      if (!isNaN(vw)) dims.rootW = vw
    }

    return dims
  }, [
    stack,
    containerNode,
    getPageDimensions,
    viewportWidth,
    containerDimensions,
    isLiveMode,
  ])

  const { rootW, rootH } = getRootDimensions()

  // Determine if the current container context is internal to the active nodes (e.g. drilled into a Prefab)
  // vs external (e.g. the Prefab Instance itself)
  const isContainerInternal = useMemo(
    () => !!containerNode && activeNodes.some((n) => n.id === containerNode.id),
    [containerNode, activeNodes]
  )

  // Calculate effective breakpoint for rendering using shared utility
  // IMPORTANT: When viewport is explicitly set, derive breakpoint from viewport width
  // When auto/100%, calculate from PAGE width (not canvas width) to ensure isolation
  // preserves the main view's breakpoint context.
  const effectiveBreakpoint = React.useMemo(() => {
    if (viewportWidth === "auto" || viewportWidth === "100%") {
      // Auto mode: Use Page Width (Design Width) to maintain context even in isolation
      const { pageWidth } = getPageDimensions()
      return getBreakpointForWidth(pageWidth)
    } else if (typeof viewportWidth === "number") {
      // Fixed viewport width: find which breakpoint this width corresponds to
      return getBreakpointForWidth(viewportWidth)
    } else {
      // Fallback to activeBreakpoint (legacy behavior)
      return activeBreakpoint || "default"
    }
  }, [viewportWidth, getPageDimensions, activeBreakpoint])

  // Debug logging for breakpoint calculation (development only)
  if (typeof window !== "undefined" && window.__VBECS_DEBUG__) {
  }

  const historyExecute = useCallback(
    (oldNodes, newNodes, oldSelection, newSelection) => {
      executeCommand(
        createUpdateNodesCommand(
          targetPrefabId,
          oldNodes,
          newNodes,
          oldSelection,
          newSelection,
          { setNodes, setPrefabs, setSelectedNodeIds }
        )
      )
    },
    [
      targetPrefabId,
      createUpdateNodesCommand,
      executeCommand,
      setNodes,
      setPrefabs,
      setSelectedNodeIds,
    ]
  )

  const historyRecord = useCallback(
    (oldNodes, newNodes, oldSelection, newSelection) => {
      recordCommand(
        createUpdateNodesCommand(
          targetPrefabId,
          oldNodes,
          newNodes,
          oldSelection,
          newSelection,
          { setNodes, setPrefabs, setSelectedNodeIds }
        )
      )
    },
    [
      targetPrefabId,
      createUpdateNodesCommand,
      recordCommand,
      setNodes,
      setPrefabs,
      setSelectedNodeIds,
    ]
  )

  const handleEditorSave = useCallback(async () => {
    if (isSaving) return
    setIsSaving(true)
    try {
      const contentToSave = {
        ...pageRef.current.content,
        ecs_nodes: nodes,
        ecs_prefabs: prefabs,
        editor_settings: {
          ...(pageRef.current.content.editor_settings || {}),
          collapsedNodes: Array.from(collapsedNodeIds),
        },
      }
      const formattedContent = await postFormatFields(
        contentToSave,
        aldooPageType
      )
      const savePayload = {
        _id: pageRef.current._id,
        content: formattedContent,
        localization: pageRef.current.localization || [],
      }
      const response = await SaveCMSContent({ payload: savePayload })
      if (response.result) {
        showNotification({
          message: "Page saved successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })
        if (onSave) onSave(response)
      } else {
        showNotification({
          message: response.error || "Failed to save page",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      console.error("Failed to save page:", error)
      showNotification({
        message: `An error occurred while saving: ${error.message}`,
        className: "bg-admin_error text-white",
      })
    } finally {
      setIsSaving(false)
    }
  }, [
    isSaving,
    setIsSaving,
    nodes,
    prefabs,
    pageRef,
    aldooPageType,
    onSave,
    collapsedNodeIds,
  ])

  // Operations
  const nodeOps = useNodeOperations({
    activeNodes,
    selectedNodeIds,
    prefabs,
    rootW,
    rootH,
    historyExecute,
    targetPrefabId,
    selectedPrefabId,
    setPrefabs,
    setSelectedNodeIds,
    setNodes,
    createCompositeCommand,
    createUpdatePrefabsCommand,
    createUpdateNodesCommand,
    executeCommand,
    containerNode,
  })

  const selectedNodesObjects = useMemo(() => {
    return selectedNodeIds
      .map((id) => {
        // Check for Virtual Node (Prefab Child)

        if (id.includes(":")) {
          const [instanceId, internalId] = id.split(":")

          const instance = activeNodes.find((n) => n.id === instanceId)

          if (instance && instance.props?.prefabId) {
            const def = prefabs.find((p) => p.id === instance.props.prefabId)

            if (def) {
              const internalNode = def.nodes.find((n) => n.id === internalId)

              if (internalNode) {
                // Merge Overrides from Instance props

                const overrides = instance.props.overrides?.[internalId] || {}

                const mergedProps = { ...internalNode.props, ...overrides }

                return {
                  ...internalNode,
                  id: id, // Composite ID
                  originalId: internalId,
                  isVirtual: true,
                  instanceId: instanceId,
                  prefabDefinitionId: instance.props.prefabId,
                  parentId: instanceId, // Treat Instance as parent for now
                  props: mergedProps,
                }
              }
            }
          }

          return null
        }

        return activeNodes.find((n) => n.id === id)
      })
      .filter(Boolean)
  }, [selectedNodeIds, activeNodes, prefabs])

  const settings = getEditorSettings()

  const propertyPanelController = usePropertyPanelController({
    selectedNodes: selectedNodesObjects,
    activeNodes,
    prefabs,
    activeBreakpoint,
    effectiveBreakpoint,
    containerNode,
    settings,
    rootW,
    rootH,
    canvasRef,
    canvasScale,
    nodeOps,
    setSelectedNodeIds,
    setActiveBreakpoint,
    setViewportWidth,
    pushContext,
    clearSelection,
    getEditorSettings,
  })

  const prefabOps = usePrefabOperations({
    prefabs,
    selectedPrefabId,
    setEditingPrefabId,
    createUpdatePrefabsCommand,
    executeCommand,
    setPrefabs,
    setSelectedPrefabId,
  })
  const hierarchyOps = useHierarchyOperations({
    activeNodes,
    selectedNodeIds,
    rootW,
    rootH,
    historyExecute,
    activeBreakpoint: effectiveBreakpoint,
  })

  // Variant Manager
  const variantManager = useVariantManager({
    variantPopup,
    activeNodes,
    effectiveBreakpoint,
    nodeOps,
    updateNodes,
  })

  // Variant handlers using unified variant manager
  const handleApplyVariant = useCallback(
    (key) => {
      variantManager.addVariant(key)
    },
    [variantManager]
  )

  const handlePreviewVariant = useCallback(
    (key) => {
      variantManager.previewVariant(key)
    },
    [variantManager]
  )

  const handleRestoreVariant = useCallback(() => {
    variantManager.restoreFromPreview()
  }, [variantManager])

  const handleVariantChange = useCallback(
    (newVariantString) => {
      variantManager.setVariant(newVariantString)
    },
    [variantManager]
  )

  const handleDisabledChange = useCallback(
    (newDisabled) => {
      if (!variantPopup?.node) return
      const rawNode = variantPopup.node
      const updates = { props: { disabledVariants: newDisabled } }

      // Handle responsive override if needed
      if (effectiveBreakpoint && effectiveBreakpoint !== "default") {
        const currentOverrides =
          rawNode.props.responsiveOverrides?.[effectiveBreakpoint] || {}
        if ("disabledVariants" in currentOverrides) {
          const { disabledVariants: _, ...cleanedOverrides } = currentOverrides
          const allOverrides = rawNode.props.responsiveOverrides || {}

          updates.props.responsiveOverrides = {
            ...allOverrides,
            [effectiveBreakpoint]: cleanedOverrides,
          }
        }
      }

      nodeOps.handlePropertyUpdate(updates, [rawNode.id])
    },
    [variantPopup, nodeOps, effectiveBreakpoint]
  )

  const handleCanvasContextMenu = useCallback(
    (e) => {
      e.preventDefault()
      if (isLiveMode) return

      const coords = CoordinateMapper.screenToCanvas(
        e.clientX,
        e.clientY,
        canvasRef,
        canvasScale
      )
      const contextId = findNodeUnderCursor({
        nodes: activeNodes,
        x: coords.x,
        y: coords.y,
        rootW,
        rootH,
        activeBreakpoint: effectiveBreakpoint,
      })

      if (contextId) {
        const node = activeNodes.find((n) => n.id === contextId)
        setCanvasContextMenu({
          x: e.clientX,
          y: e.clientY,
          node,
          items: [
            {
              label: "Variants",
              onClick: () => {
                setVariantPopup({
                  x: e.clientX,
                  y: e.clientY,
                  node,
                  componentType: node.type,
                })
              },
            },
            {
              label: "Put in Container",
              onClick: () =>
                hierarchyOps.handleContextMenuAction("put-in-container", node),
            },
          ],
        })
      }
    },
    [
      isLiveMode,
      activeNodes,
      rootW,
      rootH,
      effectiveBreakpoint,
      canvasRef,
      canvasScale,
      hierarchyOps, // Add dependency
    ]
  )

  const panning = useCanvasPanning({
    scrollContainerRef,
    panRef,
    setIsPanning: () => {},
  })
  const dragging = useNodeDragging({
    draggingRef,
    activeNodes,
    selectedNodeIds,
    updateNodes,
    setSnapLines,
    canvasScale,
    canvasRef,
    rootW,
    rootH,
    settings,
    containerNode,
    activeBreakpoint: activeBreakpoint,
    visualBreakpoint: effectiveBreakpoint,
    setSelectedNodeIds,
    collapseNode: editorState.collapseNode,
  })
  const resizing = useNodeResizing({
    resizingRef,
    activeNodes,
    updateNodes,
    canvasScale,
    rootW,
    rootH,
    settings,
    activeBreakpoint: activeBreakpoint,
    visualBreakpoint: effectiveBreakpoint,
  })
  const dragDrop = useDragAndDrop({
    canvasRef,
    canvasScale,
    prefabs,
    rootW,
    rootH,
    currentPrefabId,
    activeNodes,
    selectedNodeIds,
    historyExecute,
    containerNode,
    activeBreakpoint: effectiveBreakpoint,
    onNodeAdded: (newNodes) => {
      newNodes.forEach((node) => {
        if (node.props?.prefabId) {
          editorState.collapseNode(node.id)
        }
      })
    },
  })

  const interactionHandlers = useInteractionHandlers({
    canvasRef,
    scrollContainerRef,
    isLiveMode,
    isSpacePressed,
    canvasScale,
    activeNodes,
    rootW,
    rootH,
    selectedNodeIds,
    clearSelection,
    panning,
    startSelectionBox,
    dragging,
    resizing,
    saveInteractionState,
    activeBreakpoint: effectiveBreakpoint,
  })

  const visiblePrefabs = getFilteredPrefabs(currentPrefabId)

  const handleToggleLiveMode = useCallback(() => {
    if (!isLiveMode) {
      if (scrollContainerRef.current)
        savedEditViewRef.current = {
          scrollLeft: scrollContainerRef.current.scrollLeft,
          scrollTop: scrollContainerRef.current.scrollTop,
        }
      clearSelection()
      setIsLiveMode(true)
    } else setIsLiveMode(false)
  }, [
    isLiveMode,
    scrollContainerRef,
    savedEditViewRef,
    clearSelection,
    setIsLiveMode,
  ])

  const getBreadcrumbs = useCallback(() => {
    const crumbs = [{ id: "root", label: "Root", index: -1 }]
    let currentNodes = nodes
    stack.forEach((ctx, idx) => {
      let node = currentNodes.find((n) => n.id === ctx.id)

      // Fallback: Check if ctx.id is a Prefab Definition (Direct Edit Mode)
      if (!node) {
        const prefabDef = prefabs.find((p) => p.id === ctx.id)
        if (prefabDef) {
          crumbs.push({ id: prefabDef.id, label: prefabDef.name, index: idx })
          currentNodes = prefabDef.nodes
          return
        }
      }

      if (!node) return
      crumbs.push({ id: node.id, label: node.label || node.type, index: idx })
      if (node.props?.prefabId) {
        const def = prefabs.find((p) => p.id === node.props.prefabId)
        if (def) currentNodes = def.nodes
      }
    })
    return crumbs
  }, [nodes, stack, prefabs])

  const leftSidebarController = useLeftSidebarController({
    prefabs,
    visiblePrefabs,
    selectedPrefabId,
    prefabSearchTerm,
    editingPrefabId,
    editingName,
    activeNodes,
    selectedNodeIds,
    containerNode,
    collapsedNodeIds,
    prefabOps,
    dragDrop,
    hierarchyOps,
    interactionHandlers,
    setPrefabSearchTerm,
    setSelectedPrefabId,
    setSelectedNodeIds,
    setEditingPrefabId,
    setEditingName,
    toggleNodeExpansion,
    pushContext,
    clearSelection,
    nodeOps,
  })

  // Handle exiting prefab isolation mode and sync instances
  const handleExitPrefabIsolation = useCallback(
    (index = -1) => {
      const poppedContexts = stack.slice(index + 1)

      // Collect all prefab IDs being edited by traversing the stack to find each node
      const editedPrefabIds = new Set()

      // Start from root and traverse to find each context node
      let currentNodes = nodes

      for (let i = 0; i < stack.length; i++) {
        const ctx = stack[i]
        const node = currentNodes.find((n) => n.id === ctx.id)

        if (!node) {
          // Direct prefab definition edit
          const prefabDef = prefabs.find((p) => p.id === ctx.id)
          if (prefabDef) {
            currentNodes = prefabDef.nodes
            if (i > index) {
              editedPrefabIds.add(prefabDef.id)
            }
          }
        } else {
          // Node found - check if it's a prefab instance
          if (node.props?.prefabId) {
            const prefabDef = prefabs.find((p) => p.id === node.props.prefabId)
            if (prefabDef) {
              currentNodes = prefabDef.nodes
              if (i > index) {
                editedPrefabIds.add(prefabDef.id)
              }
            }
          }
        }
      }

      const editedPrefabDefs = Array.from(editedPrefabIds)
        .map((id) => prefabs.find((p) => p.id === id))
        .filter(Boolean)

      if (editedPrefabDefs.length > 0) {
        // Batch update scene nodes
        let updatedSceneNodes = nodes
        editedPrefabDefs.forEach((prefabDef) => {
          const sceneSync = PrefabManager.syncInstances(
            prefabDef,
            updatedSceneNodes
          )

          if (
            sceneSync.nodesToAdd.length > 0 ||
            sceneSync.nodesToUpdate.length > 0 ||
            sceneSync.nodesToRemove.length > 0
          ) {
            updatedSceneNodes = updatedSceneNodes.filter(
              (n) => !sceneSync.nodesToRemove.includes(n.id)
            )
            updatedSceneNodes = updatedSceneNodes.map((n) => {
              const updated = sceneSync.nodesToUpdate.find(
                (un) => un.id === n.id
              )
              return updated || n
            })
            updatedSceneNodes = [...updatedSceneNodes, ...sceneSync.nodesToAdd]
          }
        })

        if (updatedSceneNodes !== nodes) {
          setNodes(updatedSceneNodes)
        }

        // Batch update prefab definitions
        let updatedPrefabs = prefabs
        editedPrefabDefs.forEach((prefabDef) => {
          updatedPrefabs = updatedPrefabs.map((p) => {
            if (p.id === prefabDef.id) return p

            const pSync = PrefabManager.syncInstances(prefabDef, p.nodes)
            if (
              pSync.nodesToAdd.length > 0 ||
              pSync.nodesToUpdate.length > 0 ||
              pSync.nodesToRemove.length > 0
            ) {
              let updatedPNodes = p.nodes.filter(
                (n) => !pSync.nodesToRemove.includes(n.id)
              )
              updatedPNodes = updatedPNodes.map((n) => {
                const updated = pSync.nodesToUpdate.find((un) => un.id === n.id)
                return updated || n
              })
              updatedPNodes = [...updatedPNodes, ...pSync.nodesToAdd]
              return { ...p, nodes: updatedPNodes }
            }
            return p
          })
        })
        if (updatedPrefabs !== prefabs) {
          setPrefabs(updatedPrefabs)
        }
      }

      setContextStack(stack.slice(0, index + 1))
      clearSelection()
    },
    [
      stack,
      prefabs,
      nodes,
      setNodes,
      setPrefabs,
      clearSelection,
      setContextStack,
      getNodeChildren,
    ]
  )

  const topToolbarController = useTopToolbarController({
    selectedNodeIds,
    isLiveMode,
    isSaving,
    viewportWidth,
    activeBreakpoint,
    onSave: handleEditorSave,
    onToggleLiveMode: handleToggleLiveMode,
    onShowSettings: () => setShowSettings(true),
    onClose,
    setViewportWidth,
    setActiveBreakpoint,
    getBreadcrumbs,
    setContextAtIndex: handleExitPrefabIsolation,
  })

  const variantPopupController = useVariantPopupController({
    variantPopup,
    setVariantPopup,
    handleApplyVariant,
    handlePreviewVariant,
    handleRestoreVariant,
    handleVariantChange,
    handleDisabledChange,
    activeNodes,
    effectiveBreakpoint,
    variantManager,
  })

  const nodeItemController = useNodeItemController({
    activeNodes,
    selectedNodeIds,
    prefabs,
    isLiveMode,
    isSpacePressed,
    containerNode,
    stack,
    isContainerInternal,
    rootW,
    rootH,
    effectiveBreakpoint,
    interactionHandlers,
    nodeOps,
    setSelectedNodeIds,
    pushContext,
    clearSelection,
  })

  useKeyboardShortcuts({
    onUndo: undo,
    onRedo: redo,
    onDuplicate: nodeOps.handleDuplicate,
    onDelete: nodeOps.handleDelete,
    onSpacePress: () => setIsSpacePressed(true),
    onSpaceRelease: () => {
      setIsSpacePressed(false)
      panning.stopPan()
    },
    selectedNodeIds,
    selectedPrefabId,
    activeSidebarTab,
  })

  const stateRef = useRef({})
  useEffect(() => {
    stateRef.current = {
      activeNodes,
      selectedNodeIds,
      targetPrefabId,
      containerNode,
      canvasScale,
      isLiveMode,
      isSpacePressed,
      contextStack: stack,
      page: pageRef.current,
      settings,
    }
  }, [
    activeNodes,
    selectedNodeIds,
    targetPrefabId,
    containerNode,
    canvasScale,
    isLiveMode,
    isSpacePressed,
    stack,
    settings,
  ])

  useMouseTracking({
    stateRef,
    onMouseMove: (e) => {
      if (panRef.current) {
        panning.updatePan(e)
        return
      }
      if (isLiveMode || isSpacePressed) return
      if (selectionBox) {
        const coords = CoordinateMapper.screenToCanvas(
          e.clientX,
          e.clientY,
          canvasRef,
          canvasScale
        )
        updateSelectionBox({ currentX: coords.x, currentY: coords.y })
        const newlySelected = SelectionManager.getNodesInSelectionBox(
          activeNodes,
          { ...selectionBox, currentX: coords.x, currentY: coords.y },
          rootW,
          rootH,
          effectiveBreakpoint
        )
        setSelectedNodeIds([
          ...new Set([
            ...(selectionBox.initialSelection || []),
            ...newlySelected,
          ]),
        ])
      }
      if (draggingRef.current) dragging.updateDrag(e)
      if (resizingRef.current) resizing.updateResize(e)
    },
    onMouseUp: () => {
      const { activeNodes: currNodes, selectedNodeIds: currSelection } =
        stateRef.current
      if (
        (draggingRef.current?.hasMoved || resizingRef.current) &&
        interactionData.current.nodesBefore
      ) {
        if (interactionData.current.nodesBefore !== currNodes) {
          historyRecord(
            interactionData.current.nodesBefore,
            currNodes,
            interactionData.current.selectionBefore,
            currSelection
          )
        }
      }
      resetInteraction()
    },
  })

  useLayoutEffect(() => {
    if (!isLiveMode && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      requestAnimationFrame(() => {
        if (savedEditViewRef.current) {
          container.scrollTo(
            savedEditViewRef.current.scrollLeft,
            savedEditViewRef.current.scrollTop
          )
          savedEditViewRef.current = null
        } else {
          const scrollY = (container.scrollHeight - container.clientHeight) / 2
          const scrollX = (container.scrollWidth - container.clientWidth) / 2
          container.scrollTo(scrollX, scrollY)
        }
      })
    }
  }, [isLiveMode, stack])

  const editorInterface = {
    page: pageRef.current,

    pageId: pageRef.current?._id,

    get content() {
      return pageRef.current?.content
    },

    updateContent: editorState.updatePageContent,
  }

  // Resolve Styling for the Canvas Base (especially during isolation)

  const canvasBaseStyle = useMemo(() => {
    // If we have a container node (Isolation Mode), the container itself is rendered as a NodeItem
    // which handles background and styling. So the canvas root should be transparent/unstyled.
    if (containerNode) return {}

    // In Root Mode, we need to visualize the "Page/Viewport" boundaries.
    return {
      backgroundColor: "white",
      boxShadow: "0 0 40px rgba(0,0,0,0.1)", // Shadow to indicate "sheet"
    }
  }, [containerNode])

  // Logic to determine which nodes to render as roots on the canvas

  const displayRoots = useMemo(() => {
    if (!containerNode) return getNodeChildren(activeNodes, undefined)

    // If container exists in active nodes, we are in Normal/Box Isolation
    // Render the Container Node itself (it has x/y, so it appears in-place)
    if (activeNodes.some((n) => n.id === containerNode.id)) {
      return [containerNode]
    }

    // Otherwise, we are in Swapped Context (Prefab Mode)
    // The Definition Root(s) correspond to the Container.
    // We skip the single Definition Root to show its children directly (flattening).
    const defRoots = getNodeChildren(activeNodes, undefined)
    if (defRoots.length === 1)
      return getNodeChildren(activeNodes, defRoots[0].id)
    return defRoots
  }, [activeNodes, containerNode])

  const contentBottom = activeNodes.reduce(
    (max, node) =>
      Math.max(
        max,

        measureNode(node.id, activeNodes, rootW, rootH).y +
          measureNode(node.id, activeNodes, rootW, rootH).height
      ),

    rootH
  )

  return (
    <div
      className="fixed inset-0 z-[300] flex h-screen w-screen text-admin_text bg-admin_canvas select-none"
      style={themeVars}
    >
      <div className="flex-1 relative overflow-hidden bg-slate-50 flex flex-col">
        <TopToolbar controller={topToolbarController} />

        <div className="flex-1 relative overflow-hidden">
          {stack.length > 0 && !isLiveMode && (
            <button
              onClick={() => handleExitPrefabIsolation()}
              className="absolute top-4 right-4 z-[100] flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded shadow-xl transition-all animate-fade-in"
            >
              <Icons.ArrowLeft className="w-4 h-4" />
              exit prefab
            </button>
          )}

          <div
            ref={setScrollContainer}
            className={`w-full h-full relative overflow-auto ${
              isLiveMode ? "" : "bg-slate-50"
            } ${isSpacePressed ? "cursor-grab" : ""}`}
            onDrop={dragDrop.handleDrop}
            onDragOver={dragDrop.handleDragOver}
            onDragEnter={dragDrop.handleDragEnter}
            onDragLeave={dragDrop.handleDragLeave}
            onMouseDown={interactionHandlers.handleCanvasMouseDown}
            onContextMenu={handleCanvasContextMenu}
          >
            <div
              className={`relative ${
                isLiveMode
                  ? "w-full h-full overflow-auto flex justify-center bg-zinc-900"
                  : "min-h-[200vh] min-w-[200vw] flex items-center justify-center"
              }`}
            >
              <div
                id="vbecs-canvas-root"
                data-scale={canvasScale}
                ref={canvasRef}
                className="relative" // Simplified class, removed visual styles
                style={{
                  width:
                    isLiveMode && viewportWidth === "100%" ? "100%" : rootW,
                  height: isLiveMode ? "100%" : rootH,
                  transform: isLiveMode ? undefined : `scale(${canvasScale})`,
                  transformOrigin: "top left",
                  minHeight: isLiveMode ? "100%" : undefined,
                  ...canvasBaseStyle,
                }}
              >
                {nodeItemController &&
                  displayRoots.map((node) => (
                    <NodeItem
                      key={node.id}
                      controller={nodeItemController.createNodeController(node)}
                    />
                  ))}

                {/* Ghost Preview for Prefab Drag */}
                {dragDrop.dragPreview &&
                  !isLiveMode &&
                  dragDrop.dragPreview.nodes &&
                  (() => {
                    const previewRoot = dragDrop.dragPreview.nodes.find(
                      (n) => n.id === dragDrop.dragPreview.rootId
                    )
                    if (!previewRoot) return null

                    return (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                            zIndex: 10000,
                            opacity: 0.6,
                          }}
                        >
                          {nodeItemController && (
                            <NodeItem
                              controller={nodeItemController.createPreviewController(
                                previewRoot,
                                dragDrop.dragPreview.nodes
                              )}
                              isInteractive={false}
                            />
                          )}
                        </div>
                      </>
                    )
                  })()}

                {!isLiveMode && <SnapLinesOverlay lines={snapLines} />}
                {!isLiveMode && (
                  <div
                    style={{
                      position: "absolute",
                      top: contentBottom + 300,
                      height: 1,
                      width: 1,
                      pointerEvents: "none",
                    }}
                  />
                )}
                {!isLiveMode && selectedNodeIds.length > 0 && (
                  <GizmoOverlay
                    activeNodes={activeNodes}
                    selectedNodeIds={selectedNodeIds}
                    rootW={rootW}
                    rootH={rootH}
                    onResizeMouseDown={
                      interactionHandlers.handleResizeMouseDown
                    }
                    onAxisDragMouseDown={(e, id, axis) =>
                      interactionHandlers.handleNodeMouseDown(
                        e,
                        id,
                        axis,
                        setSelectedNodeIds
                      )
                    }
                    canvasRef={canvasRef}
                    canvasScale={canvasScale}
                    activeBreakpoint={effectiveBreakpoint}
                  />
                )}
                {!isLiveMode && <SelectionBox selectionBox={selectionBox} />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {canvasContextMenu && (
        <ContextMenu
          x={canvasContextMenu.x}
          y={canvasContextMenu.y}
          items={
            canvasContextMenu.items || [
              {
                label: "Group",
                onClick: () =>
                  hierarchyOps.handleContextMenuAction(
                    "put-in-container",
                    canvasContextMenu.node
                  ),
              },
              {
                label: "Ungroup",
                onClick: () =>
                  hierarchyOps.handleContextMenuAction(
                    "ungroup-container",
                    canvasContextMenu.node
                  ),
              },
            ]
          }
          onClose={() => setCanvasContextMenu(null)}
        />
      )}

      {variantPopup && variantPopupController && (
        <VariantPopup controller={variantPopupController} />
      )}

      {!isLiveMode && (
        <LeftSidebar
          activeSidebarTab={activeSidebarTab}
          onTabChange={setActiveSidebarTab}
          controller={leftSidebarController}
        />
      )}

      {!isLiveMode && (
        <div className="w-72 bg-admin_paper border-l border-admin_bordered z-10 shadow-lg pr-2">
          <PropertyPanel controller={propertyPanelController} />
        </div>
      )}

      {showSettings && (
        <PageSettingsDialog
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          editor={editorInterface}
        />
      )}
    </div>
  )
}

export default VBECSEditor
